import * as Sentry from '@sentry/browser'
import { fetchAPI } from 'libs/apis'
import useAxios from 'axios-hooks'
import type { UseAxiosResult } from 'axios-hooks'
import i18n from 'config/i18n'
import * as amplitude from '@amplitude/analytics-browser'

import { LIMIT_PAGE } from 'helpers/constants/common'

import type {
  IStockKeepingUnitSingleProps,
  IStockKeepingUnitQueryParams,
  IStockKeepingUnitFormPayload,
  IStockKeepingUnitDetail,
  IFile,
  INameImg,
  listUploadImg,
} from './model'
import type { IProduct } from 'features/Product'
import { ILoaderOption, STATUS } from 'types'
import { OptionsType } from 'react-select'
import { AxiosRequestConfig } from 'axios'
import { IParamForSubmit } from 'libs/apis/helper'
import { createEventProperties } from 'helpers/amplitudeTracker'

export type AppendFormData =
  | IFile[]
  | Array<string | Blob>
  | { id: string; value: string }[]
  | string
  | Blob

export const uploadImg = async (
  idSKU: string,
  image: { image: listUploadImg[] },
): Promise<{ body: INameImg[] } | unknown> => {
  const form = new FormData()
  const appendedForm = appendForm(form, image)
  const response = await fetchAPI<INameImg[]>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'POST',
    path: `/v1/image/${idSKU}/fileStorage`,
    options: {
      headers: { 'Content-Type': 'multipart/form-data' },
      data: appendedForm,
    },
  })

  return {
    body: response.body,
  }
}

export const findAll = async (
  params: IStockKeepingUnitQueryParams & IParamForSubmit,
  options?: AxiosRequestConfig,
): Promise<{ body: IStockKeepingUnitSingleProps[]; totals: number }> => {
  const withDefaultParams: IParamForSubmit = {
    _limit: LIMIT_PAGE,
    isActive: params?.isActive || true,
    ...params,
    ...(params._sort === 'category' && {
      _sort: i18n.language === 'th' ? 'categoryNameTh' : 'categoryNameEn',
    }),
  }

  if (withDefaultParams?.isActive === STATUS.ALL) {
    delete withDefaultParams.isActive
  }

  const newParams = {
    ...withDefaultParams,
    code: params.code?.trim(),
  }

  const stockKeepingUnits = await fetchAPI<IStockKeepingUnitSingleProps[]>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1.1/sku',
    params: newParams,
    options,
  })

  if (stockKeepingUnits.status !== 200) {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return {
    body: stockKeepingUnits.body,
    totals: Number(stockKeepingUnits.headers['x-total-count']),
  }
}

export const getProductCategories = (
  nameTh: string,
): Promise<OptionsType<ILoaderOption>> => {
  return new Promise(async (resolve, reject) => {
    const newParams = {
      _limit: 10,
      isActive: true,
      ...(nameTh && { 'nameTh[like]': `%${nameTh.trim()}%` }),
    }

    try {
      const response = await fetchAPI<IProduct[]>({
        apiUrl: window._env_.REACT_APP_API_URL,
        method: 'GET',
        path: `/v1/product/category`,
        params: newParams,
      })

      const mapper: OptionsType<ILoaderOption> = response.body.map(
        (item: IProduct) => ({
          key: item.id,
          label: item.name,
          value: item.id,
        }),
      )

      resolve(mapper)
    } catch (e) {
      reject(e)
    }
  })
}

export const useFindAll = (
  params?: IStockKeepingUnitQueryParams,
): UseAxiosResult<IStockKeepingUnitSingleProps[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/sku`

  return useAxios({
    url: path,
    params,
  })
}

export const getDataSKU = async (
  idSKU: string,
): Promise<IStockKeepingUnitDetail | unknown> => {
  const response = await fetchAPI({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: `/v1.1/sku/${idSKU}`,
  })

  if (response.status === 200) {
    return response.body
  } else {
    throw new Error(i18n.t('error:notFound.title'))
  }
}

export const useFindOneById = (
  id: string,
): UseAxiosResult<IStockKeepingUnitDetail> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1.1/sku/${id}`

  return useAxios({
    url: path,
  })
}

const appendForm = <T>(form: FormData, data: T): FormData => {
  Object.entries<AppendFormData>(data as never).map(async ([key, value]) => {
    switch (key) {
      case 'files':
        const imageFiles = value as IFile[]

        imageFiles.forEach((item, index) => {
          if (item.isThumbnail) {
            form.append('thumbnailIndex', `${index}`)
          }

          form.append(key, item.file, `image${index}.jpg`)
        })

        break

      case 'photos':
        const photos = value as Array<string | Blob>
        photos.forEach((item: string | Blob) => {
          form.append(key, item)
        })

        break

      case 'categories':
        const categories = value as { id: string; nameTh?: string }[]

        categories.forEach((item) => {
          if (item.id) {
            form.append('categories', item.id)
          }

          if (!item.id && item.nameTh) {
            form.append('categoryNameTh', item.nameTh)
          }
        })

        break

      case 'image':
        const files = value as IFile[]

        files.forEach((item: IFile, index: number) => {
          if (item.isThumbnail) {
            form.append(key, `${index}`)
          }

          form.append(key, item.file, `image${index}.jpg`)
        })

        break

      case 'deleteFiles':
        const deleteFiles = value as { id: string }[]

        deleteFiles.forEach((item) => {
          if (item.id) {
            form.append(key, item.id)
          }
        })

        break

      case 'isActive':
        const status = value ? 'true' : 'false'
        form.append(key, status)

        break

      default:
        form.append(key, value as string | Blob)

        break
    }
  })

  return form
}

export const create = async (
  body: IStockKeepingUnitFormPayload,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const form = new FormData()
    const appendedForm = appendForm<IStockKeepingUnitFormPayload>(form, body)

    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: '/v1.1/sku',
      options: {
        headers: { 'Content-Type': 'multipart/form-data' },
        data: appendedForm,
      },
    })

    const isSuccess = response.status === 200 || response.status === 201
    if (isSuccess) {
      const eventProperties = { ...createEventProperties(), sku: body }
      amplitude.track('FixTab Backoffice: Create SKU', eventProperties)

      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

export const update = async (
  id: string,
  body: IStockKeepingUnitFormPayload,
  handleOpenSuccess: () => void,
): Promise<void> => {
  try {
    const response = await fetchAPI({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'PATCH',
      path: `/v1.1/sku/${id}`,
      options: { data: body },
    })

    const isSuccess = response.status === 200 || response.status === 201
    if (isSuccess) {
      const eventProperties = { ...createEventProperties(), sku: body }
      const action = body.isActive ? 'Update' : 'Delete'
      amplitude.track(`FixTab Backoffice: ${action} SKU`, eventProperties)

      handleOpenSuccess()
    } else {
      throw new Error(i18n.t('error:notFound.title'))
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

export const downloadSkuTemplate = async (): Promise<Blob> => {
  const response = await fetchAPI<{
    arrayBuffer(): Blob
  }>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1.2/sku/import/template',
    responseType: 'blob',
  })

  return response.body.arrayBuffer()
}

export const downloadProductExport = async (): Promise<void> => {
  try {
    const response = await fetchAPI<Blob>({
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'GET',
      path: '/v1/product/exports',
      responseType: 'blob',
    })

    const blob = new Blob([response.body], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = 'products.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()

    window.URL.revokeObjectURL(url)
  } catch (error) {
    Sentry.captureException(error)
    console.error('Failed to download product export:', error)
  }
}
